import type { ImportCandidate, Language } from '@kidshub/contracts';

/**
 * What the family already has of a listed video, for the audio being asked for.
 *
 * - `NEW` — nothing; this is what a parent came back to the channel for.
 * - `QUEUED` — a running import is already bringing it in.
 * - `HELD` — in the library in this language, or in one nobody labelled. An
 *   unlabelled track is nearly always the channel's own language, and treating
 *   it as a match errs towards not downloading the same episode twice.
 * - `HELD_IN_OTHER` — in the library, but heard in other languages. Still new
 *   for this import: Turkish and English are two things a parent may want.
 */
export type CandidateState =
  | { kind: 'NEW' }
  | { kind: 'QUEUED' }
  | { kind: 'HELD' }
  | { kind: 'HELD_IN_OTHER'; languages: Language[] };

export const candidateState = (
  candidate: ImportCandidate,
  audioLanguage: Language,
): CandidateState => {
  if (candidate.isQueued) {
    return { kind: 'QUEUED' };
  }

  const languages = candidate.inLibrary.map((copy) => copy.audioLanguage);
  if (languages.length === 0) {
    return { kind: 'NEW' };
  }
  if (languages.some((language) => language === null || language === audioLanguage)) {
    return { kind: 'HELD' };
  }

  return {
    kind: 'HELD_IN_OTHER',
    languages: [...new Set(languages.filter((language) => language !== null))],
  };
};

/** Whether "select all" should take it: anything this import would actually add. */
export const isWorthImporting = (state: CandidateState): boolean =>
  state.kind === 'NEW' || state.kind === 'HELD_IN_OTHER';
